import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'

import Link from 'next/link'
import { ArrowRight, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'

export type SectionTone = 'cream' | 'paper' | 'burgundy'

interface SplitFeatureProps {
    id?: string
    label: string
    title: string
    description: ReactNode
    points?: Array<string | { text: string, Icon?: LucideIcon }>
    visual: ReactNode
    reverse?: boolean
    tone?: SectionTone
    link?: { href: string, label: string }
    aside?: ReactNode
    headingLevel?: 'h2' | 'h3'
}

const SURFACES: Record<SectionTone, string> = {
    cream: 'bg-cream text-burgundy',
    paper: 'bg-paper text-burgundy',
    burgundy: 'bg-burgundy text-cream'
}

/** Editorial two-column section: copy on one side, product visual on the other. */
export const SplitFeature: FC<SplitFeatureProps> = ({
    id,
    label,
    title,
    description,
    points = [],
    visual,
    reverse = false,
    tone = 'cream',
    link,
    aside,
    headingLevel: Heading = 'h2'
}): ReactNode => {
    const dark = tone === 'burgundy'

    return (
        <section id={id} aria-labelledby={id ? `${id}-title` : undefined} className={cn('relative scroll-mt-20 overflow-hidden', SURFACES[tone])}>
            <Container className='grid gap-16 py-28 md:py-36 lg:grid-cols-12 lg:items-center lg:gap-10'>
                <div className={cn('lg:col-span-5', reverse && 'lg:order-2 lg:col-start-8')}>
                    <p
                        data-reveal=''
                        className={cn('flex items-center gap-4 font-mono text-[11px] font-medium uppercase tracking-[0.2em]', dark ? 'text-beige' : 'text-stone-dark')}
                    >
                        <span aria-hidden='true' className={cn('size-1.5 rounded-full', dark ? 'bg-gold' : 'bg-gold-dark')} />
                        {label}
                    </p>

                    <Heading
                        id={id ? `${id}-title` : undefined}
                        data-reveal=''
                        className='mt-7 font-heading text-display-lg font-medium'
                    >
                        {title}
                    </Heading>

                    <div data-reveal='' className={cn('mt-6 max-w-lg text-lg leading-relaxed', dark ? 'text-cream/70' : 'text-stone-dark')}>
                        {description}
                    </div>

                    {points.length > 0 && (
                        <ul data-reveal='' className='mt-9 space-y-3.5'>
                            {points.map(point => {
                                const item = typeof point === 'string' ? { text: point } : point
                                const Icon = item.Icon ?? Check

                                return (
                                    <li key={item.text} className={cn('flex items-start gap-3 text-[15px]', dark ? 'text-cream/85' : 'text-burgundy')}>
                                        <span className={cn('mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full', dark ? 'bg-cream/10 text-gold' : 'bg-burgundy/[0.06] text-gold-dark')}>
                                            <Icon aria-hidden='true' className='size-3.5' />
                                        </span>
                                        <span className='pt-0.5'>{item.text}</span>
                                    </li>
                                )
                            })}
                        </ul>
                    )}

                    {aside}

                    {link && (
                        <Link
                            href={link.href}
                            className={cn(
                                'group mt-10 inline-flex items-center gap-2 border-b pb-1 text-sm font-medium transition-colors duration-300',
                                dark ? 'border-cream/30 text-cream hover:border-cream' : 'border-burgundy/30 text-burgundy hover:border-burgundy'
                            )}
                        >
                            {link.label}
                            <ArrowRight aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:translate-x-0.5' />
                        </Link>
                    )}
                </div>

                <div
                    data-reveal='scale'
                    className={cn('lg:col-span-7', reverse ? 'lg:order-1 lg:col-start-1' : 'lg:col-start-6 lg:pl-6')}
                >
                    <div className='mx-auto max-w-[640px] sm:px-6 lg:px-0'>
                        {visual}
                    </div>
                </div>
            </Container>
        </section>
    )
}
